import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, XCircle, Loader2, Award, Calendar, User, Building, MapPin, Phone, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Verify() {
  const { language } = useLanguage();
  const { certificateNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      try {
        const response = await axios.get(`${API}/verify/${certificateNumber}`);
        setResult(response.data);
      } catch (error) {
        setResult({
          valid: false,
          message: language === 'ja' ? '証明書の照合に失敗しました' : 'Failed to verify certificate'
        });
      } finally {
        setLoading(false);
      }
    };
    if (certificateNumber) verifyCertificate();
  }, [certificateNumber, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#00A651] mx-auto mb-4" />
          <p className="text-gray-600">{language === 'ja' ? '証明書を照合中...' : 'Verifying certificate...'}</p>
        </div>
      </div>
    );
  }

  const photoSrc = result?.profileImage
    ? (result.profileImage.startsWith('http') ? result.profileImage : result.profileImage.startsWith('/api/') ? `${BACKEND_URL}${result.profileImage}` : `${BACKEND_URL}/api/uploads/${result.profileImage}`)
    : null;

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-md mx-auto px-4">

        {/* Certificate Card */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden" style={{ border: '4px solid #00A651' }}>
          <div style={{ border: '2px solid #D4AF37', margin: 2, borderRadius: 4 }}>

            {/* Status Header */}
            <div className={`py-3 px-4 text-center ${result?.valid ? 'bg-[#00A651]' : 'bg-red-500'}`}>
              <div className="flex items-center justify-center gap-2 text-white">
                {result?.valid ? <Shield className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                <span className="font-bold text-sm">
                  {result?.valid
                    ? (language === 'ja' ? '✓ 有効な証明書' : '✓ Valid Certificate')
                    : (language === 'ja' ? '✗ 無効な証明書' : '✗ Invalid Certificate')}
                </span>
              </div>
            </div>

            {result?.valid ? (
              <div className="p-5">
                {/* Title */}
                <div className="text-center mb-4">
                  <p className="text-[10px] text-gray-400 tracking-widest">CERTIFICATE OF COMPLETION</p>
                  <h1 className="text-2xl font-bold text-[#00A651] tracking-[0.2em]">修 了 証</h1>
                </div>

                {/* Certificate Number */}
                <div className="text-center mb-4">
                  <span className="inline-block border border-[#00A651] rounded px-3 py-1 text-xs">
                    <span className="text-gray-500">No. </span>
                    <span className="font-mono font-bold text-gray-800">{result.certificateNumber}</span>
                  </span>
                </div>

                {/* Photo + Personal Info */}
                <div className="flex gap-4 mb-4">
                  {/* Photo Frame */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full border-2 border-[#00A651] overflow-hidden bg-gray-50">
                      {photoSrc ? (
                        <img src={photoSrc} alt="証明写真" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="h-8 w-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Personal Details */}
                  <div className="flex-1 space-y-2">
                    <div>
                      <p className="text-[10px] text-gray-400">氏名</p>
                      <p className="text-lg font-bold text-gray-900 leading-tight">{result.holderName}</p>
                      {result.holderNameKana && <p className="text-xs text-gray-400">{result.holderNameKana}</p>}
                    </div>
                    {result.birthDate && (
                      <div>
                        <p className="text-[10px] text-gray-400">生年月日</p>
                        <p className="text-sm text-gray-700">{result.birthDate}</p>
                      </div>
                    )}
                    {result.address && (
                      <div>
                        <p className="text-[10px] text-gray-400">住所</p>
                        <p className="text-xs text-gray-700 leading-tight">{result.address}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#D4AF37] my-3" />

                {/* Course Info */}
                <div className="bg-[#e6f6ec] border border-[#00A651] rounded-lg py-3 px-4 text-center mb-3">
                  <p className="text-[10px] text-[#00A651] mb-1">講習名</p>
                  <p className="font-bold text-[#00A651]">{result.courseName}</p>
                </div>

                {/* Detail Grid */}
                <div className="space-y-2">
                  {result.completionDate && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-[#00A651] flex-shrink-0" />
                      <span className="text-gray-500 text-xs w-16">修了日</span>
                      <span className="font-medium text-gray-800">{result.completionDate}</span>
                    </div>
                  )}
                  {result.company && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-[#00A651] flex-shrink-0" />
                      <span className="text-gray-500 text-xs w-16">所属</span>
                      <span className="font-medium text-gray-800">{result.company}</span>
                    </div>
                  )}
                  {result.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-[#00A651] flex-shrink-0" />
                      <span className="text-gray-500 text-xs w-16">電話</span>
                      <span className="font-medium text-gray-800">{result.phone}</span>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-3" />

                {/* Issuer */}
                <div className="text-center">
                  <p className="text-xs font-bold text-gray-800">{result.issuedBy}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">JWSEA Foundation</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 px-6">
                <XCircle className="h-16 w-16 text-red-300 mx-auto mb-4" />
                <p className="text-gray-600">{result?.message}</p>
                <p className="text-sm text-gray-400 mt-4">
                  {language === 'ja'
                    ? '証明書番号をご確認の上、再度お試しください。'
                    : 'Please check the certificate number and try again.'}
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t text-center">
              <p className="text-[10px] text-gray-400">
                {language === 'ja'
                  ? 'この照合システムは JWSEA Foundation が提供しています。'
                  : 'This verification system is provided by JWSEA Foundation.'}
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
